import { v4 as uuid } from 'uuid';
import { imageKit, type BaseBridge } from '@meitu/wasm-lib';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas } from '@/utils/file-kit';
import type { ILayerProps, ISize } from '@/version-types';

interface IGLArgs {
    /** 图层数据 */
    layer: ILayerProps;
    /** 图层存储结果的属性 */
    props: { url: string };
    /** 需要获取的结果大小 */
    resultSize?: ISize;
    /** 基础桥接 */
    baseBridge?: BaseBridge;
    /** 在应用 GL 结果之前，可以进行一些处理 */
    beforeGetGLResult?: () => Promise<HTMLCanvasElement | null>;
}

/** GL 操作工具 */
export const glKit = {
    /** 应用 GL 结果到图层属性上 */
    async applyGLResult(args: IGLArgs) {
        const { props } = args;
        const url = await glKit.getGLResult(args);
        if (url) {
            props.url = url;
            return true;
        }
        return false;
    },

    /** 获取 GL 结果，并重置 GL 标记 */
    async getGLResult(args: Omit<IGLArgs, 'props'>) {
        const { layer, baseBridge, resultSize } = args;
        const isGLProcessed = glKit.isGLProcessed(layer);

        glKit.clearGL(layer);

        if (!isGLProcessed) return '';

        const beforeCanvas = await args.beforeGetGLResult?.();
        const canvas =
            beforeCanvas ||
            (baseBridge ? (await baseBridge.getGLResult())?.canvas : (imageKit.getGLCanvas() as HTMLCanvasElement));

        let resCanvas = canvas;
        if (resultSize && canvas && (canvas.width !== resultSize.width || canvas.height !== resultSize.height)) {
            resCanvas = createCanvas(resultSize.width, resultSize.height, canvas).canvas;
        }

        return resCanvas ? base64Kit.getCode(resCanvas.toDataURL(), uuid()) : '';
    },

    /** 打开 GL 渲染 */
    openGL(layer: ILayerProps) {
        layer.__isUseWebGL = true;
    },

    /** 标记 GL 已处理 */
    processGL(layer: ILayerProps) {
        layer.__isWebGLProcessed = true;
    },

    /** 是否已经处理过 GL */
    isGLProcessed(layer: ILayerProps) {
        return layer.__isWebGLProcessed;
    },

    /** 是否使用 GL */
    isUseGL(layer: ILayerProps) {
        return layer.__isUseWebGL;
    },

    /** 清除 GL 标记 */
    clearGL(layer: ILayerProps) {
        delete layer.__isWebGLProcessed;
        delete layer.__isUseWebGL;
    },

    /**
     * 底层 GL 在超过这个值的容器中会缩放处理。bridge 取到的是缩放后的 canvas，
     * 如果不限制，直接绘制 GL canvas 会丢失部分像素，导致结果不完整。
     */
    scaleResourceIfNeeded(canvas: HTMLCanvasElement | HTMLImageElement): HTMLCanvasElement {
        const MAX_PIXELS = 32490000; // 5700 * 5700，限制画布最大像素数量，超过则进行缩放处理
        const { width, height } = canvas;
        const totalPixels = width * height;

        if (totalPixels <= MAX_PIXELS) {
            if (canvas instanceof HTMLImageElement) {
                return createCanvas(width, height, canvas).canvas;
            }
            return canvas;
        }

        const ratio = Math.sqrt(MAX_PIXELS / totalPixels);
        const newWidth = Math.floor(width * ratio);
        const newHeight = Math.floor(height * ratio);

        return createCanvas(newWidth, newHeight, canvas).canvas;
    },
};
